import React, { useState, useEffect } from 'react';

const PROXY = 'https://crabplace-fb.netlify.app/.netlify/functions';
const ACCOUNT_ID = 'act_47263248';
const ACCOUNT_NUM = '47263248';

function fmt$(n) {
  if (n == null) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtMonth(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function fmtRange(start, stop) {
  if (!start || !stop) return '—';
  const s = new Date(start + 'T00:00:00');
  const e = new Date(stop + 'T00:00:00');
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(s)} – ${fmt(e)}, ${e.getFullYear()}`;
}

export default function MetaInvoices() {
  const [months, setMonths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${PROXY}/invoices?account_id=${ACCOUNT_ID}`)
      .then(r => r.json())
      .then(res => {
        if (res?.error) throw new Error(res.error.message || JSON.stringify(res.error));
        if (res?.success === false) throw new Error(res?.error?.error?.message || res?.message || 'Failed to fetch invoices');

        const list = res?.data?.data ?? res?.data ?? [];
        const arr = Array.isArray(list) ? list : [];
        // Newest first
        arr.sort((a, b) => new Date(b.date_start || 0) - new Date(a.date_start || 0));
        setMonths(arr);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const billingUrl = `https://business.facebook.com/billing_hub/payment_activity?asset_id=${ACCOUNT_NUM}`;

  // Running total
  const totalSpend = months ? months.reduce((sum, m) => sum + parseFloat(m.spend || 0), 0) : 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Meta Invoices</h2>
        <a
          href={billingUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, color: 'var(--blue, #2563eb)', textDecoration: 'none', fontWeight: 600 }}
        >
          Open Meta Billing Hub &rarr;
        </a>
      </div>

      {/* Total KPI */}
      {!loading && !error && months && months.length > 0 && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="kpi-label">Total Spend (Last 12 Mo)</div>
            <div className="kpi-value">{fmt$(totalSpend)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Months</div>
            <div className="kpi-value">{months.length}</div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Monthly Billing</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && (!months || months.length === 0) && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No billing data found.</div>
        )}
        {!loading && !error && months && months.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Month</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Billing Period</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Impressions</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Clicks</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Spend</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m, i) => {
                  // Link to Meta billing hub filtered to that month
                  const invoiceLink = `${billingUrl}&start_date=${m.date_start}&end_date=${m.date_stop}`;

                  return (
                    <tr key={m.date_start || i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>
                        {fmtMonth(m.date_start)}
                      </td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-600)' }}>
                        {fmtRange(m.date_start, m.date_stop)}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {m.impressions ? parseInt(m.impressions).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {m.clicks ? parseInt(m.clicks).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--navy)', fontWeight: 600 }}>
                        {fmt$(m.spend)}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <a
                          href={invoiceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, color: 'var(--blue, #2563eb)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          View Invoice
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
